import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../translations';

export default function Footer() {
  const currentYear = new Date().getFullYear();
  const { language } = useLanguage();
  const t = translations[language];

  // Contact Form State
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    description: '',
    website_url: '' // Honeypot
  });
  const [formStatus, setFormStatus] = useState('idle'); // idle, loading, success, error
  const [formError, setFormError] = useState('');

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setFormStatus('loading');
    setFormError('');

    if (formData.website_url) {
      setFormStatus('success');
      return;
    }

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: formData.name,
          phone: formData.phone,
          email: formData.email,
          description: formData.description,
          website_url: formData.website_url
        })
      });

      if (!response.ok) {
        throw new Error('Der opstod en fejl ved afsendelse af formularen. Prøv venligst igen.');
      }

      setFormStatus('success');
      setFormData({
        name: '',
        phone: '',
        email: '',
        description: '',
        website_url: ''
      });
    } catch (error) {
      console.error('Form submission error:', error);
      setFormStatus('error');
      setFormError(error.message || 'Kunne ikke sende besked. Prøv igen senere.');
    }
  };

  return (
    <footer className="relative bg-navy-900 pt-48 pb-12 overflow-hidden">
      {/* Background Image visible at the top */}
      <div className="absolute inset-0 z-0 h-[300px]">
        <img
          src="https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=80,format=auto/assets/8297297f-eccf-4eba-a924-6f973a63cdc4/scandinavian-office.jpg"
          alt="Contact Background"
          className="w-full h-full object-cover opacity-15"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-navy-900"></div>
      </div>

      {/* Top Band: Overlapping Floating Cards */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-20 -mt-24 mb-16">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {/* Card 1: Telefon */}
          <a 
            href="tel:30655509" 
            className="bg-white rounded-none p-8 shadow-xl border border-bluegray/20 text-center flex flex-col items-center justify-center group hover:-translate-y-1 transition-transform duration-300 w-full h-full"
          >
            <div className="h-14 w-14 bg-navy-50 text-petrol flex items-center justify-center rounded-none mb-4 group-hover:bg-petrol group-hover:text-white transition-colors duration-300">
              <i className="fa-regular fa-phone-volume text-2xl"></i>
            </div>
            <span className="text-[10px] font-bold uppercase tracking-widest text-navy-900/40 mb-2">{t.footer.phone}</span>
            <span className="text-base font-bold text-navy-900 group-hover:text-petrol transition-colors">
              30 65 55 09
            </span>
          </a>

          {/* Card 2: E-mail */}
          <a 
            href="mailto:rvi@insightly.dk" 
            className="bg-white rounded-none p-8 shadow-xl border border-bluegray/20 text-center flex flex-col items-center justify-center group hover:-translate-y-1 transition-transform duration-300 w-full h-full"
          >
            <div className="h-14 w-14 bg-navy-50 text-petrol flex items-center justify-center rounded-none mb-4 group-hover:bg-petrol group-hover:text-white transition-colors duration-300">
              <i className="fa-regular fa-envelope-open text-2xl"></i>
            </div>
            <span className="text-[10px] font-bold uppercase tracking-widest text-navy-900/40 mb-2">{t.footer.email}</span>
            <span className="text-base font-bold text-navy-900 group-hover:text-petrol transition-colors break-all">
              rvi@insightly.dk
            </span>
          </a>

          {/* Card 3: Adresse */}
          <a 
            href="https://maps.google.com/?q=Dronningemaen+68,+5700+Svendborg" 
            target="_blank" 
            rel="noopener noreferrer"
            className="bg-white rounded-none p-8 shadow-xl border border-bluegray/20 text-center flex flex-col items-center justify-center group hover:-translate-y-1 transition-transform duration-300 w-full h-full"
          >
            <div className="h-14 w-14 bg-navy-50 text-petrol flex items-center justify-center rounded-none mb-4 group-hover:bg-petrol group-hover:text-white transition-colors duration-300">
              <i className="fa-regular fa-map-location-dot text-2xl"></i>
            </div>
            <span className="text-[10px] font-bold uppercase tracking-widest text-navy-900/40 mb-2">{t.footer.address}</span>
            <span className="text-sm font-bold text-navy-900 group-hover:text-petrol transition-colors">
              {t.footer.addressValue}
            </span>
          </a>

          {/* Card 4: LinkedIn */}
          <a 
            href="https://dk.linkedin.com/in/rene-vilsgaard/da" 
            target="_blank" 
            rel="noopener noreferrer"
            className="bg-white rounded-none p-8 shadow-xl border border-bluegray/20 text-center flex flex-col items-center justify-center group hover:-translate-y-1 transition-transform duration-300 w-full h-full"
          >
            <div className="h-14 w-14 bg-navy-50 text-petrol flex items-center justify-center rounded-none mb-4 group-hover:bg-petrol group-hover:text-white transition-colors duration-300">
              <i className="fa-brands fa-linkedin-in text-2xl"></i>
            </div>
            <span className="text-[10px] font-bold uppercase tracking-widest text-navy-900/40 mb-2">{t.footer.linkedin}</span>
            <span className="text-base font-bold text-navy-900 group-hover:text-petrol transition-colors">
              {t.footer.linkedinConnect}
            </span>
          </a>
        </div>
      </div>

      {/* Bottom Band: Split Two-Column Layout */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start mb-16">
          {/* Left Column - Form with Floating Labels and Underline Borders */}
          <div className="lg:col-span-6 bg-navy-950/40 p-8 sm:p-10 border border-white/10 rounded-none">
            <h3 className="font-serif text-2xl font-bold text-white mb-2">{t.contactForm.title}</h3>
            <p className="text-sm text-offwhite/60 mb-8">
              {t.footer.formDesc}
            </p>

            {formStatus === 'success' ? (
              <div className="bg-white/5 border border-white/10 text-white p-6 rounded-none text-center">
                <i className="fa-solid fa-circle-check text-4xl text-petrol mb-3"></i>
                <h4 className="font-serif text-lg font-bold mb-1">{t.contactForm.success}</h4>
                <button
                  onClick={() => setFormStatus('idle')}
                  className="mt-4 text-xs font-bold uppercase tracking-wider text-petrol hover:text-white underline transition-colors"
                >
                  {t.footer.formSuccessNew}
                </button>
              </div>
            ) : (
              <form onSubmit={handleFormSubmit} className="space-y-8">
                {/* Honeypot field */}
                <div className="sr-only absolute" aria-hidden="true">
                  <label htmlFor="website_url">Do not fill this field if you are human</label>
                  <input
                    type="text"
                    id="website_url"
                    name="website_url"
                    value={formData.website_url}
                    onChange={handleFormChange}
                    tabIndex="-1"
                    autoComplete="off"
                  />
                </div>

                {/* Name */}
                <div className="relative z-0 w-full group">
                  <input
                    type="text"
                    name="name"
                    id="name"
                    required
                    value={formData.name}
                    onChange={handleFormChange}
                    className="block py-2.5 px-0 w-full text-sm text-white bg-transparent border-0 border-b-2 border-offwhite/20 appearance-none focus:outline-none focus:ring-0 focus:border-petrol peer"
                    placeholder=" "
                  />
                  <label
                    htmlFor="name"
                    className="peer-focus:font-medium absolute text-sm text-offwhite/60 duration-300 transform -translate-y-6 scale-75 top-3 -z-10 origin-[0] peer-focus:left-0 peer-focus:text-petrol peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-focus:scale-75 peer-focus:-translate-y-6"
                  >
                    {t.contactForm.name} *
                  </label>
                </div>

                {/* Phone */}
                <div className="relative z-0 w-full group">
                  <input
                    type="tel"
                    name="phone"
                    id="phone"
                    required
                    value={formData.phone}
                    onChange={handleFormChange}
                    className="block py-2.5 px-0 w-full text-sm text-white bg-transparent border-0 border-b-2 border-offwhite/20 appearance-none focus:outline-none focus:ring-0 focus:border-petrol peer"
                    placeholder=" "
                  />
                  <label
                    htmlFor="phone"
                    className="peer-focus:font-medium absolute text-sm text-offwhite/60 duration-300 transform -translate-y-6 scale-75 top-3 -z-10 origin-[0] peer-focus:left-0 peer-focus:text-petrol peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-focus:scale-75 peer-focus:-translate-y-6"
                  >
                    {t.contactForm.phone} *
                  </label>
                </div>

                {/* Email */}
                <div className="relative z-0 w-full group">
                  <input
                    type="email"
                    name="email"
                    id="email"
                    required
                    value={formData.email}
                    onChange={handleFormChange}
                    className="block py-2.5 px-0 w-full text-sm text-white bg-transparent border-0 border-b-2 border-offwhite/20 appearance-none focus:outline-none focus:ring-0 focus:border-petrol peer"
                    placeholder=" "
                  />
                  <label
                    htmlFor="email"
                    className="peer-focus:font-medium absolute text-sm text-offwhite/60 duration-300 transform -translate-y-6 scale-75 top-3 -z-10 origin-[0] peer-focus:left-0 peer-focus:text-petrol peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-focus:scale-75 peer-focus:-translate-y-6"
                  >
                    {t.contactForm.email} *
                  </label>
                </div>

                {/* Description */}
                <div className="relative z-0 w-full group">
                  <textarea
                    name="description"
                    id="description"
                    required
                    rows="4"
                    value={formData.description}
                    onChange={handleFormChange}
                    className="block py-2.5 px-0 w-full text-sm text-white bg-transparent border-0 border-b-2 border-offwhite/20 appearance-none focus:outline-none focus:ring-0 focus:border-petrol peer resize-none"
                    placeholder=" "
                  ></textarea>
                  <label
                    htmlFor="description"
                    className="peer-focus:font-medium absolute text-sm text-offwhite/60 duration-300 transform -translate-y-6 scale-75 top-3 -z-10 origin-[0] peer-focus:left-0 peer-focus:text-petrol peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-focus:scale-75 peer-focus:-translate-y-6"
                  >
                    {t.contactForm.message} *
                  </label>
                </div>

                {formStatus === 'error' && (
                  <div className="bg-red-500/10 border border-red-500/20 text-red-200 p-4 rounded-none text-sm flex items-center gap-2.5">
                    <i className="fa-solid fa-circle-exclamation text-base"></i>
                    <span>{formError}</span>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={formStatus === 'loading'}
                  className="w-full bg-petrol hover:bg-petrol-dark text-navy-900 py-4 rounded-none text-xs font-bold uppercase tracking-wider transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                >
                  {formStatus === 'loading' ? (
                    <>
                      <i className="fa-solid fa-spinner animate-spin"></i>
                      <span>{t.contactForm.sending}</span>
                    </>
                  ) : (
                    <>
                      <i className="fa-solid fa-paper-plane"></i>
                      <span>{t.contactForm.send}</span>
                    </>
                  )}
                </button>
              </form>
            )}
          </div>

          {/* Right Column - Info */}
          <div className="lg:col-span-6 space-y-8 lg:pl-8 text-white">
            <span className="text-xs uppercase tracking-[0.25em] text-petrol font-bold">// {t.footer.contactTitle}</span>
            <h2 className="text-3xl sm:text-4xl font-serif font-medium text-white leading-tight">
              {t.about.contactCta}
            </h2>
            <p className="text-xl italic font-serif text-offwhite/90 leading-relaxed">
              {t.footer.contactQuote}
            </p>
            <p className="text-offwhite/70 text-base leading-relaxed font-sans">
              {t.footer.contactText}
            </p>
            
            {/* Social Icons Row */}
            <div className="pt-6 border-t border-white/10">
              <p className="text-[10px] font-bold uppercase tracking-widest text-offwhite/40 mb-4">// {t.footer.socialMedia}</p>
              <div className="flex gap-4">
                <a 
                  href="https://dk.linkedin.com/in/rene-vilsgaard/da" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="h-12 w-12 bg-white/5 hover:bg-petrol hover:text-navy-900 text-petrol flex items-center justify-center rounded-none transition-all duration-300 text-xl border border-white/10"
                  aria-label="LinkedIn"
                >
                  <i className="fa-brands fa-linkedin-in"></i>
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-white/10 pt-8 flex flex-col sm:flex-row justify-between items-center gap-4 text-xs text-offwhite/50">
          <p>© {currentYear} Insightly ApS. {t.footer.rights} · {t.footer.cvr} 43779362</p>
          <p className="font-sans uppercase tracking-widest text-[10px]">
            {t.footer.advisory}
          </p>
        </div>
      </div>
    </footer>
  );
}
